//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { EthAddr, TxHash } from '@shared/SchemaTypes'
import { Contract, ContractTransactionResponse, Signer } from 'ethers'

import { ActivityEnum, AlertLevelEnum, PoolTypeEnum, RoleEnum } from '@shared/Enums'

import { poolActions } from '@react/redux/poolSlice'

import { logOT } from '@shared/loggingShared'

import { ThunkAction, UnknownAction } from '@reduxjs/toolkit'
import {
  IPoolBaseAbi,
  PoolAbi,
  PoolControllerAbi,
  PoolControllerDynamicAbi,
  PoolControllerFlexAbi,
  PoolDynamicAbi,
  PoolFlexAbi,
} from '@shared/Abis'

import { assertRole, dispatchError, OtStoreState } from '@react/redux/store'

import { otAssert } from '@shared/conversions'

import { alertActions } from '@react/redux/alertSlice'
import { navActions } from '@react/redux/navSlice'
import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import { txUUID, wsServerActionPoolTX, wsServerActionPoolTXRequest } from '@shared/WsServerActions'
import { PoolController, PoolControllerDynamic, PoolDynamic } from '@typechain-types/index'

export enum PoolStateChangeEnum {
  ACTIVATE = 'ACTIVATE',
  PAUSE_DEPOSITS = 'PAUSE_DEPOSITS',
  RESUME_DEPOSITS = 'RESUME_DEPOSITS',
  PAUSE_WITHDRAWS = 'PAUSE_WITHDRAWS',
  RESUME_WITHDRAWS = 'RESUME_WITHDRAWS',
}
export const poolStateChangeTx =
  (props: {
    poolAddr: EthAddr
    poolState: PoolStateChangeEnum
  }): ThunkAction<void, OtStoreState, unknown, UnknownAction> =>
  async (dispatch, getState): Promise<void> => {
    const { poolAddr, poolState } = props
    const activity = ActivityEnum.POOL_ACTIVATE
    try {
      logOT.debug(activity)

      const { webSocket, userConnection } = getState().chainState
      const { chainConfigurationName } = getState().chainState.chainConfiguration
      otAssert(userConnection, 'userConnection is not defined')
      const { roleEnum, signer } = userConnection

      assertRole(roleEnum, RoleEnum.POOL_ADMIN)
      otAssert(signer, 'signer is not defined')

      await poolStateChange(webSocket, signer as Signer, poolAddr, poolState, chainConfigurationName)

      dispatch(poolActions.activatePoolSuccess({ poolAddr }))
      await new Promise((resolve) => setTimeout(resolve, 500))

      dispatch(navActions.navigateTo({ url: `/pools/${poolAddr}` }))
    } catch (e: any) {
      dispatch(poolActions.creatPoolFail())
      dispatch(
        alertActions.addAlert({
          title: 'Could not activate pool.',
          description: 'Please wait and try again',
          createdAt: Date.now() / 1000,
          dueDateTime: Date.now() / 1000,
          txHash: '',
          sourceObjectAddr: ETH_ADDR_ZERO,
          alertLevel: AlertLevelEnum.ERROR,
        })
      )
      await dispatchError(dispatch, e, activity, 1037)
    }
  }
export const poolStateChange = async (
  webSocket: WebSocket | undefined,
  poolAdminSigner: Signer,
  poolAddr: EthAddr,
  poolState: PoolStateChangeEnum,
  chainConfigurationName: string
): Promise<void> => {
  logOT.debug('activatePool ')
  const uuid = txUUID()
  webSocket?.send(wsServerActionPoolTXRequest(ActivityEnum.POOL_ACTIVATE, poolAddr, uuid, chainConfigurationName))

  const poolBaseContract = new Contract(poolAddr, IPoolBaseAbi, poolAdminSigner)
  const poolType = Number(await poolBaseContract.poolType())

  if (poolType === PoolTypeEnum.Dynamic) {
    const poolContract = new Contract(poolAddr, PoolDynamicAbi, poolAdminSigner) as unknown as PoolDynamic
    const poolControllerAddr = await poolContract.poolControllerDynamic()

    otAssert(poolControllerAddr)

    const poolControllerContract = new Contract(
      poolControllerAddr,
      PoolControllerDynamicAbi,
      poolAdminSigner
    ) as unknown as PoolControllerDynamic

    let tx2: ContractTransactionResponse
    if (poolState === PoolStateChangeEnum.ACTIVATE) {
      tx2 = await poolControllerContract.connect(poolAdminSigner).activatePool()
    } else if (poolState === PoolStateChangeEnum.PAUSE_DEPOSITS) {
      tx2 = await poolControllerContract.connect(poolAdminSigner).pauseDeposits()
    } else if (poolState === PoolStateChangeEnum.RESUME_DEPOSITS) {
      tx2 = await poolControllerContract.connect(poolAdminSigner).resumeDeposits()
    } else if (poolState === PoolStateChangeEnum.PAUSE_WITHDRAWS) {
      tx2 = await poolControllerContract.connect(poolAdminSigner).pauseWithdraws()
    } else if (poolState === PoolStateChangeEnum.RESUME_WITHDRAWS) {
      tx2 = await poolControllerContract.connect(poolAdminSigner).resumeWithdraws()
    } else {
      throw new Error('Unknown pool state')
    }

    webSocket?.send(
      wsServerActionPoolTX(ActivityEnum.POOL_ACTIVATE, poolAddr, uuid, tx2.hash as TxHash, chainConfigurationName)
    )

    await tx2.wait(1)
  } else if (poolType === PoolTypeEnum.Term) {
    const poolContract = new Contract(poolAddr, PoolAbi, poolAdminSigner)
    const poolControllerAddr = await poolContract.poolController()

    otAssert(poolControllerAddr)

    const poolControllerContract = new Contract(poolControllerAddr, PoolControllerAbi, poolAdminSigner)

    const tx2 = await (poolControllerContract.connect(poolAdminSigner) as unknown as PoolController).activatePool()

    webSocket?.send(
      wsServerActionPoolTX(ActivityEnum.POOL_ACTIVATE, poolAddr, uuid, tx2.hash as TxHash, chainConfigurationName)
    )

    await tx2.wait(1)
    logOT.debug('activatePool success', poolAddr)
  } else if (poolType === PoolTypeEnum.FlexibleRate) {
    const poolFlexContract = new Contract(poolAddr, PoolFlexAbi, poolAdminSigner)
    const poolControllerAddr = await poolFlexContract.poolController()
    otAssert(poolControllerAddr)

    const poolControllerFlex = new Contract(poolControllerAddr, PoolControllerFlexAbi, poolAdminSigner)

    const tx2 = await poolControllerFlex.activatePool()
    webSocket?.send(
      wsServerActionPoolTX(ActivityEnum.POOL_ACTIVATE, poolAddr, uuid, tx2.hash as TxHash, chainConfigurationName)
    )
    tx2.wait(1)

    logOT.debug('activatePool success', poolAddr)
  } else {
    throw new Error('Unknown pool type')
  }
}
